import { loadOptionalChunk } from '@/common/chunkReload';
import { planEncode, type EncodePlan } from '@/utils/imageEncodePlan';

/**
 * 업로드 공통 이미지 압축 — **폭이 큰 사진만 비율대로 줄이고, 눈으로 차이 없는 화질로 용량을 줄인다.**
 *
 * - 폭이 2000px 를 넘으면 가로·세로를 같은 비율(70%)로 줄인 뒤 인코딩한다(2026-10-19 사용자 결정, 크기 판단은 [planEncode]).
 *   최대 크기로 자르지 않고 비율만 줄이므로 가로세로 비가 그대로이고, 폭 2000px 이하(캡처·포스터·긴 홍보 이미지)는 크기를 건드리지 않는다.
 *   "긴 변 2560 제한"은 세로로 긴 이미지 폭을 183px 까지 줄여 글자를 망가뜨려 쓰지 않는다. 운영 사진 실측: 3213×5712 4.5MB → 2249×3998 1.3MB.
 * - 화질 0.8(Compressor.js 기본값) 로 **한 번만** 인코딩한다(줄인 크기로 그린 뒤 한 번). 용량을 맞추려고 화질을 반복해서 낮추지 않는다
 *   (browser-image-compression 은 화질을 0.5 근처까지 깎고, iOS 에서는 해상도도 몰래 줄였다).
 * - JPEG·WebP 는 형식을 유지하고, PNG 는 투명도를 지키는 WebP 로 바꾼다. GIF 는 애니메이션이 깨지므로 건드리지 않는다.
 * - **WebP 를 못 만드는 브라우저(아이폰·아이패드 사파리, 앱 웹뷰)** 는 libwebp WASM(`@jsquash/webp`, 처음 쓸 때만 불러옴)으로
 *   같은 화질(quality 80 손실, 알파 유지, 팔레트·near-lossless 같은 색 바꾸는 옵션 없음)로 만든다 — 크롬 결과와 바이트까지 같다(2026-10-19 시험).
 *   인코더를 못 불러오거나 인코딩이 실패하면 예전처럼 투명하지 않은 PNG 만 JPEG, 나머지는 원본.
 * - 줄인 뒤 가로·세로 중 하나라도 16,383px(WebP 한계)을 넘으면 WebP 를 만들지 않고 원본을 올린다 — 크롬은 넘는 부분을 말없이 잘라 버린다.
 *   JPEG 입력은 JPEG 한계(65,535)라 그대로 압축한다.
 * - 결과가 원본보다 크거나 같거나, 원래 크기로 그리지 못했거나(iOS 캔버스 면적 상한 등), 어느 방법으로도 인코딩하지 못하면
 *   원본 파일을 그대로 쓴다. 압축 중 예외도 원본으로 대체한다.
 * - 최종 파일이 서버 한도(UPLOAD_IMAGE_MAX_BYTES)를 넘으면 요청을 보내지 않고 [ImageTooLargeError] 를 던진다.
 */
const QUALITY = 0.8;
/** WASM 인코더 화질 — 크롬 toBlob('image/webp', 0.8) 과 같은 libwebp 값 */
const WASM_WEBP_QUALITY = 80;

/** 서버 multipart 한도 — core/src/main/resources/application.yml `max-file-size: 15MB` */
export const UPLOAD_IMAGE_MAX_BYTES: number = 15 * 1024 * 1024;

const toMB = (bytes: number): string => (bytes / 1024 / 1024).toFixed(1);

/** 최종 파일이 업로드 한도를 넘을 때 — message 를 그대로 사용자에게 보여 준다 */
export class ImageTooLargeError extends Error {
    constructor(size: number, limit: number = UPLOAD_IMAGE_MAX_BYTES) {
        super(`이미지 용량이 너무 커서 올릴 수 없어요. ${Math.floor(limit / 1024 / 1024)}MB 이하 이미지로 올려 주세요. (현재 ${toMB(size)}MB)`);
        this.name = 'ImageTooLargeError';
    }
}

/** 확장자를 변환 포맷에 맞춘다 — 서버가 파일명 확장자로 형식을 판단한다. */
const withExtension = (filename: string, extension: string): string =>
    `${filename.replace(/\.[^.]+$/, '')}.${extension}`;

/** EXIF 방향이 반영된 원래 크기로 디코딩한다(현재 브라우저는 img 에 방향을 자동 적용한다). */
const loadImage = (file: File): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
        const url: string = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
            URL.revokeObjectURL(url);
            resolve(img);
        };
        img.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error('이미지를 읽지 못했습니다.'));
        };
        img.src = url;
    });

/** 투명한 픽셀이 하나라도 있으면 true — JPEG 로 바꾸면 투명 부분이 검게 나온다 */
const hasTransparency = (ctx: CanvasRenderingContext2D, width: number, height: number): boolean => {
    const data: Uint8ClampedArray = ctx.getImageData(0, 0, width, height).data;
    for (let i = 3; i < data.length; i += 4) {
        if (data[i] < 255) return true;
    }
    return false;
};

const toBlob = (canvas: HTMLCanvasElement, type: string): Promise<Blob | null> =>
    new Promise((resolve) => canvas.toBlob(resolve, type, QUALITY));

/**
 * 브라우저가 WebP 를 직접 만들 수 있는지 — 못 만들면 toBlob 이 PNG 를 돌려준다(사파리).
 * 큰 이미지로 알아보면 쓸데없이 PNG 를 한 번 만들게 되므로 1×1 로 한 번만 확인한다.
 */
let nativeWebpSupport: Promise<boolean> | null = null;
const canEncodeWebpNatively = (): Promise<boolean> => {
    if (!nativeWebpSupport) {
        const probe: HTMLCanvasElement = document.createElement('canvas');
        probe.width = 1;
        probe.height = 1;
        nativeWebpSupport = toBlob(probe, 'image/webp').then((blob) => blob?.type === 'image/webp');
    }
    return nativeWebpSupport;
};

/** libwebp WASM 으로 WebP 인코딩 — 손실 압축 quality 80, 나머지는 libwebp 기본값(알파 품질 100, near-lossless 끔, 팔레트 없음) */
const encodeWebpWasm = (data: ImageData): Promise<Blob> =>
    // 인코더 조각(encode.js 와 그 안의 codec 조각)을 못 불러와도 화면을 새로고침하지 않는다 — 호출하는 쪽이 대체 동작으로 넘어간다
    loadOptionalChunk(async () => {
        const { default: encode } = await import('@jsquash/webp/encode.js');
        const buffer: ArrayBuffer = await encode(data, { quality: WASM_WEBP_QUALITY, lossless: 0, near_lossless: 100 });
        return new Blob([buffer], { type: 'image/webp' });
    });

/**
 * [plan] 크기로 그려 [plan].output 형식으로 한 번 인코딩한다. 그 크기로 그리지 못하거나 어느 방법으로도 못 만들면 null.
 * WebP: 브라우저 → (못 만들면) WASM → (그것도 실패하면) 투명 픽셀 없는 PNG 만 JPEG.
 */
const encodeOnce = async (file: File, img: HTMLImageElement, plan: EncodePlan): Promise<Blob | null> => {
    const { width, height } = plan;
    const canvas: HTMLCanvasElement = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    try {
        const ctx: CanvasRenderingContext2D | null = canvas.getContext('2d');
        // 면적 상한을 넘으면 사파리는 컨텍스트를 주지 않는다 — 줄여 그리지 않고 원본을 쓴다
        if (!ctx || canvas.width !== width || canvas.height !== height) return null;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, width, height);

        if (plan.output === 'image/jpeg') {
            const jpeg: Blob | null = await toBlob(canvas, 'image/jpeg');
            return jpeg?.type === 'image/jpeg' ? jpeg : null;
        }

        if (await canEncodeWebpNatively()) {
            const webp: Blob | null = await toBlob(canvas, 'image/webp');
            if (webp?.type === 'image/webp') return webp;
        }
        try {
            return await encodeWebpWasm(ctx.getImageData(0, 0, width, height));
        } catch (error) {
            console.warn('WebP 인코더 실패 — 예전 방식으로 대체합니다:', error);
        }
        // 대체: JPEG 로 바꾸면 투명 부분이 검게 나오므로 투명하지 않은 PNG 만
        if (file.type !== 'image/png' || hasTransparency(ctx, width, height)) return null;
        const jpeg: Blob | null = await toBlob(canvas, 'image/jpeg');
        return jpeg?.type === 'image/jpeg' ? jpeg : null;
    } finally {
        // 사파리 캔버스 메모리 해제
        canvas.width = 0;
        canvas.height = 0;
    }
};

export const compressImage = async (file: File): Promise<File> => {
    // 애니메이션 GIF 는 압축하면 첫 프레임만 남는다
    if (file.type !== 'image/png' && file.type !== 'image/jpeg' && file.type !== 'image/webp') return file;
    try {
        const img: HTMLImageElement = await loadImage(file);
        // 16,383px 넘는 WebP 등 — 원본 그대로
        const plan: EncodePlan | null = planEncode(file.type, img.naturalWidth, img.naturalHeight);
        if (!plan) return file;
        const encoded: Blob | null = await encodeOnce(file, img, plan);
        if (!encoded || encoded.size >= file.size) return file;
        const name: string = encoded.type === file.type ? file.name : withExtension(file.name, encoded.type === 'image/webp' ? 'webp' : 'jpg');
        return new File([encoded], name, { type: encoded.type });
    } catch (error) {
        console.warn('이미지 압축 실패 — 원본으로 올립니다:', error);
        return file;
    }
};

/** 업로드 직전 처리 — 압축 후 한도를 넘으면 요청 없이 [ImageTooLargeError] */
export const prepareImageForUpload = async (file: File): Promise<File> => {
    const prepared: File = await compressImage(file);
    if (prepared.size > UPLOAD_IMAGE_MAX_BYTES) throw new ImageTooLargeError(prepared.size);
    return prepared;
};
